<template>
  <div ref="baseMenu" class="base-menu dropdown-menu">
    <slot name="menu-activator" :on="{ click: handleFocus }" :open="isMenuOpen" />
    <Teleport :to="attach ?? 'body'">
      <Transition name="slide-fade">
        <ul
          v-if="isMenuOpen"
          ref="menuContent"
          v-click-outside="handleBlur"
          :class="` ${contentClass ?? ''} base-menu--options bg-white menu-content menu-items`"
          :style="menuOptionsPosition"
        >
          <li
            v-for="(item, index) in items"
            :key="index"
            :style="{ height: `${itemHeight}px` }"
            class="pa-1"
          >
            <router-link
              v-if="item.path && !item.isDisabled"
              class="d-flex align-center text-decoration-none inherit--text text-capitalize inherit--font justify-start h-100 d-flex align-center menu-option"
              :to="{ ...item }"
            >
              <div v-if="iconPosition === 'left' && item?.icon">
                <v-img
                  v-if="iconIsImage(item.icon)"
                  :width="item.iconSize"
                  :height="item.iconSize"
                  :src="item.icon"
                />
                <v-icon v-else :size="item.iconSize">{{ item.icon }}</v-icon>
              </div>
              {{ item.label }}
              <div v-if="iconPosition === 'right' && item?.icon">
                <v-img
                  v-if="iconIsImage(item.icon)"
                  :width="item.iconSize"
                  :height="item.iconSize"
                  :src="item.icon"
                />
                <v-icon v-else :size="item.iconSize">{{ item.icon }}</v-icon>
              </div>
            </router-link>
            <BaseButton
              v-else-if="!item.hide"
              variant="text"
              height-variant="auto"
              height="100%"
              :class="`${item.textClass} ${selectedItemIndex === index && !noVisibleSelection ? 'selected' : ''} w-100 inherit--text justify-start inherit--font text-capitalize menu-option`"
              :left-icon="iconPosition === 'left' ? item.icon : ''"
              :right-icon="iconPosition === 'right' ? item.icon : ''"
              :icon-size="item.iconSize"
              :disabled="item.isDisabled"
              @click="handleMenuItemClick({ ...item, index, mouseEvent: $event })"
            >
              {{ item.label }}
            </BaseButton>
          </li>
        </ul>
      </Transition>
    </Teleport>
  </div>
</template>
<script setup lang="ts">
import { ref, nextTick, onUnmounted, watch, computed, onMounted } from 'vue'
import { iconIsImage, isDefined } from '@/utils/helpers'
import { useScrollTo } from '@/hooks'
import BaseButton from './BaseButton.vue'

const props = defineProps<{
  items: {
    icon?: string
    iconSize?: string | number
    label: string
    textClass?: string
    path?: string
    query?: any
    action?: () => void
    hide?: boolean
    isDisabled?: boolean
    value?: string | number
  }[]
  iconPosition?: string
  minWidth?: number
  alignMenuLeft?: boolean
  itemHeight?: number | string
  contentClass?: string
  position?: { x: number; y: number }
  attach?: string
  ignoreClickOutside?: string
  noVisibleSelection?: boolean
}>()
const emit = defineEmits(['input', 'change', 'collapse'])

const boundModel = defineModel()
const selectedItemIndex = ref()

const isMenuOpen = ref(false)
const baseMenu = ref()
const menuContent = ref()

const { scrollToSelected } = useScrollTo(menuContent, '.selected', 'instant')

const menuOptionsPosition = computed(() => {
  if (!boundModel.value || !props.position) return
  // const { top, bottom, left, right } = menuContent.value.getBoundingClientRect()

  return {
    top: `${props.position.y}px`,
    left: `${props.position.x}px`,
    minWidth: `${props.minWidth}px`
  }
})

const handleFocus = (event: FocusEvent) => {
  event.preventDefault()
  event.stopPropagation()
  const target = event.currentTarget as HTMLElement

  const { top, bottom, left, right } = target.getBoundingClientRect()

  isMenuOpen.value = true

  nextTick(() => {
    const overlayContainer =
      (target.closest('.v-overlay.v-overlay--active') as HTMLElement) ||
      (target.closest('.v-card') as HTMLElement)

    const openedMenuInAnOverlay = !!overlayContainer

    if (openedMenuInAnOverlay) {
      const overlayZIndex = getComputedStyle(overlayContainer).zIndex
      menuContent.value.style.setProperty('z-index', `${overlayZIndex + 1}`, 'important')
    }

    const menuContentHeight = menuContent.value.clientHeight
    const menuWillOverflowWindowBottom = window.innerHeight - bottom < menuContentHeight
    const menuWillOverflowWindowTop = top < menuContentHeight

    if (menuWillOverflowWindowTop && menuWillOverflowWindowBottom) {
      menuContent.value.style.top = `${top - menuContentHeight / 2}px`
    } else if (menuWillOverflowWindowBottom) {
      menuContent.value.style.bottom = `${window.innerHeight - top}px`
    } else {
      menuContent.value.style.top = `${bottom}px`
    }
    if (!props.alignMenuLeft) {
      menuContent.value.style.right = `${window.innerWidth - right}px`
    } else {
      menuContent.value.style.left = `${left}px`
    }
    menuContent.value.style.minWidth = `${props.minWidth}px`
  })
}

const handleBlur = (args: any) => {
  const elementClassList = Object.values(args.target.classList) as string[]
  if (
    elementClassList.some(
      (className: string) =>
        props.ignoreClickOutside && className.includes(props.ignoreClickOutside)
    )
  )
    return
  if (isMenuOpen.value) {
    isMenuOpen.value = false
    emit('collapse')
  }
}

const handleMenuItemClick = (item: {
  action?: (mouseEvent: any) => void
  index: number
  mouseEvent: MouseEvent
}) => {
  selectedItemIndex.value = item.index
  nextTick(() => {
    if (item.action) item.action(item.mouseEvent)
    else emit('change', item)
    isMenuOpen.value = false
  })
}

const cleanUp = () => {
  const menuContent = document.querySelector('.v-application--wrap > ul.menu-content')
  menuContent?.remove()
}

watch(isMenuOpen, () => {
  if (isMenuOpen.value === false) {
    const listWithPointerEventsDisabled = document.querySelector(
      '.list-content.pointer-events--none'
    )
    if (listWithPointerEventsDisabled)
      listWithPointerEventsDisabled.classList.remove('pointer-events--none')
  } else {
    nextTick(scrollToSelected)
  }
})

watch(boundModel, (value) => {
  isMenuOpen.value = !!value
})

onMounted(() => {
  const selectedMenuItemIndex = props.items.findIndex((item) => item.value === boundModel.value)
  if (isDefined(boundModel.value) && selectedItemIndex) {
    selectedItemIndex.value = selectedMenuItemIndex
  }
})

onUnmounted(cleanUp)
</script>

<style scoped lang="scss">
.base-menu {
  position: relative;
  width: max-content;
}
.menu-content {
  list-style: none;
  position: absolute;
  z-index: 100;
  padding-left: unset;
  box-shadow: 0px 1px 5px 0px rgba(38, 62, 87, 0.07);
  filter: drop-shadow(1px 0.5px 13px rgba(38, 62, 87, 0.07));

  &.align-bottom {
    transform: translateY(-100%);
  }
}
.slide-fade-enter-active {
  transition: all 0.3s ease;
}
.slide-fade-leave-active {
  transition: all 0.3s ease;
}
.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}
.selected {
  background-color: rgb(var(--v-theme-blue_100));
  color: rgb(var(--v-theme-blue_600)) !important;
}
</style>
